import { useQuery } from '@apollo/client';
import { Disclosure } from '@headlessui/react';
import { MinusIcon, PlusIcon } from '@heroicons/react/24/outline';
import { gql } from '__generated__/gql';
import classNames from 'classnames';
import posthog from 'posthog-js';
import { FC, useEffect } from 'react';

import ErrorMessage from 'components/ErrorMessage';

import Badge from 'primitives/Badge';
import Button from 'primitives/Button';
import LoadingIndicator from 'primitives/LoadingIndicator';

import alIndiaLogo from 'media/alIndiaLogoGrey.png';

import constants from 'utils/constants';

import { discoverDealsFaqs } from './DiscoverDealsFAQs';

const DISCOVER_DEALS_QUERY = gql(`
  query DiscoverDeals {
    discoverDeals {
      id
      publicData {
        name
        description
        leads
        round
        founders
        dealInviteLink
      }
      companyInformation {
        company {
          image
          website
        }
      }
      syndicate {
        name
        image
      }
    }
  }
`);

const DiscoverDeals: FC = () => {
  const { loading, error, data, refetch } = useQuery(DISCOVER_DEALS_QUERY);

  useEffect(() => {
    // Capture UTM parameters from URL
    const urlParams = new URLSearchParams(window.location.search);
    const utmSource = urlParams.get('utm_source');
    const utmContent = urlParams.get('utm_content');

    // Send UTM data to PostHog
    if (utmSource || utmContent) {
      posthog.capture('discover_deals_page_viewed', {
        utm_source: utmSource || null,
        utm_content: utmContent || null,
      });
    }
  }, []);

  if (loading) return <LoadingIndicator />;

  if (error || !data) return <ErrorMessage error={error} refetch={refetch} />;

  if (!data.discoverDeals) return <ErrorMessage title="No deals found" refetch={refetch} />;

  const deals = data.discoverDeals;

  return (
    <div>
      <CoverImage />

      {/* Public deals */}
      <div className="flex flex-col gap-y-20 mb-32 px-16 pb-24">
        {deals.map(deal => (
          <div
            key={deal.id}
            className="p-8 bg-gray-50 border rounded-md transition-transform duration-200 hover:scale-[101%] flex flex-col h-full w-[40%] mx-auto"
          >
            <img
              className="w-40 h-40 rounded-md p-1 cursor-pointer"
              src={deal.companyInformation.company.image ?? constants.ALT_AVATAR_URL}
              alt={deal.publicData?.name || 'Deal'}
              style={{ objectFit: 'contain', objectPosition: 'center' }}
              onClick={() => {
                window.open(deal.companyInformation.company.website || '', '_blank');
              }}
            />
            <div className="space-y-1 mt-2">
              <p className="font-bold text-2xl">{deal.publicData?.name || 'Deal'}</p>
              <p className="text-base text-gray-500">
                {deal.publicData?.description || 'No description'}
              </p>
            </div>
            <div className="flex flex-col mt-auto pt-8 space-y-2">
              {deal?.publicData?.leads && (
                <div className="flex flex-row space-x-2">
                  <p className="text-base text-gray-500 self-center min-w-[15%]">Led by</p>
                  <InfoBadge description={deal.publicData?.leads} color="indigo" />
                </div>
              )}
              {deal?.publicData?.founders && (
                <div className="flex flex-row space-x-2">
                  <p className="text-base text-gray-500 self-center w-[15%]">Founders</p>
                  <InfoBadge description={deal.publicData?.founders} color="yellow" />
                </div>
              )}
              {deal?.publicData?.round && (
                <div className="flex flex-row space-x-2">
                  <p className="text-base text-gray-500 self-center w-[15%]">Round</p>
                  <InfoBadge description={deal.publicData?.round} color="orange" />
                </div>
              )}
            </div>
            <Button
              variant="custom"
              className="mt-16 w-32 hover:bg-[rgb(16,44,50)] text-white text-base py-3 bg-black"
              onClick={() => {
                window.open(deal.publicData?.dealInviteLink || '', '_blank');
              }}
            >
              View deal
            </Button>
          </div>
        ))}
      </div>

      {/* FAQs */}
      <div
        id="faqs"
        className="mt-6 my-20 max-w-4xl mx-auto p-5 w-full divide-y divide-gray-900/10 bg-gray-50 rounded-xl"
      >
        <h2 className="text-2xl font-semibold leading-10 tracking-tight text-gray-800">
          Frequently asked questions
        </h2>
        <dl className="mt-5 space-y-6 divide-y divide-gray-900/10">
          <Disclosure as="div" className="pt-6" defaultOpen={true}>
            {({ open }) => (
              <>
                <dt>
                  <Disclosure.Button className="flex w-full items-start justify-between text-left text-gray-700">
                    <span
                      className={classNames('text-lg font-semibold leading-7', open && 'underline')}
                    >
                      FAQs
                    </span>
                    <span className="ml-6 flex h-7 items-center">
                      {open ? (
                        <MinusIcon className="h-6 w-6" aria-hidden="true" />
                      ) : (
                        <PlusIcon className="h-6 w-6" aria-hidden="true" />
                      )}
                    </span>
                  </Disclosure.Button>
                </dt>
                <Disclosure.Panel as="dd" className="mt-2 pr-12">
                  {discoverDealsFaqs.map(faq => (
                    <Disclosure as="div" key={faq.question} className="pt-6">
                      {({ open }) => (
                        <>
                          <dt>
                            <Disclosure.Button className="flex w-full items-start justify-between text-left text-gray-700">
                              <span className="text-base font-semibold leading-7">
                                {faq.question}
                              </span>
                              <span className="ml-6 flex h-7 items-center">
                                {open ? (
                                  <MinusIcon className="h-6 w-6" aria-hidden="true" />
                                ) : (
                                  <PlusIcon className="h-6 w-6" aria-hidden="true" />
                                )}
                              </span>
                            </Disclosure.Button>
                          </dt>
                          <Disclosure.Panel as="dd" className="mt-2 pr-12">
                            <div className="text-base leading-7 text-gray-600">{faq.answer}</div>
                          </Disclosure.Panel>
                        </>
                      )}
                    </Disclosure>
                  ))}
                </Disclosure.Panel>
              </>
            )}
          </Disclosure>
        </dl>
      </div>
    </div>
  );
};

const InfoBadge: FC<{ description: string; color: string }> = ({ description, color }) => {
  if (!description) return <Badge label={'-'} color={color} className="!text-2xl" />;
  return <Badge label={description} color={color} className="!text-base" />;
};

const CoverImage: FC = () => {
  return (
    <div className="bg-white mb-16 relative bg-no-repeat bg-[length:500px_500px] bg-[120%_5rem]">
      <div className="px-6 pb-12 pt-8">
        <img
          src={alIndiaLogo}
          alt="Discover Deals"
          className="mx-auto w-[20rem] h-auto object-contain p-4 rounded-md mb-8"
        />
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-xl font-bold tracking-tight sm:text-5xl">Discover Deals</h2>
          <div className="mt-6">
            <h3 className="text-lg inline">New to AngelList India? </h3>
            <h3
              className="text-lg inline hover:cursor-pointer text-indigo-600"
              onClick={() => {
                const el = document.getElementById('faqs');
                if (el) {
                  el.scrollIntoView({ behavior: 'smooth' });
                }
              }}
            >
              View our FAQs
            </h3>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DiscoverDeals;
